﻿import { useEffect, useMemo, useState } from "react";
import {
  commitPublicWebpay,
  createPublicWebpayIntent,
  searchPublicDebts,
} from "@/lib/publicPaymentsApi";
import SearchForm from "./components/SearchForm";
import DocumentList from "./components/DocumentList";
import PaymentSummary from "./components/PaymentSummary";
import { ContractInfo, PaymentDocument } from "./types";

const LAST_SEARCH_KEY = "apr_public_payment_last_identifier";

export default function PagarPage() {
  const [contract, setContract] = useState<ContractInfo | null>(null);
  const [documents, setDocuments] = useState<PaymentDocument[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [paying, setPaying] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);

  const orderedDocuments = useMemo(
    () =>
      [...documents].sort((a, b) => {
        const byDueDate = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        if (byDueDate !== 0) return byDueDate;
        return new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime();
      }),
    [documents],
  );

  const selectedDocuments = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return orderedDocuments.filter((d) => selectedSet.has(d.id));
  }, [orderedDocuments, selectedIds]);

  useEffect(() => {
    void handleTransbankReturn();
  }, []);

  const handleResults = (c: ContractInfo, docs: PaymentDocument[]) => {
    setContract(c);
    setDocuments(docs);
    setSelectedIds([]);
    setFeedback(null);
    localStorage.setItem(LAST_SEARCH_KEY, c.contractNumber);
  };

  const handleClear = () => {
    setContract(null);
    setDocuments([]);
    setSelectedIds([]);
    setFeedback(null);
  };

  const handleToggle = (id: string) => {
    const orderedIds = orderedDocuments.map((doc) => doc.id);
    const clickedIndex = orderedIds.indexOf(id);
    if (clickedIndex < 0) return;

    const selectedSet = new Set(selectedIds);

    if (selectedSet.has(id)) {
      // Si desmarca una boleta, también se desmarcan las más nuevas.
      setSelectedIds(orderedIds.slice(0, clickedIndex));
      setFeedback(null);
      return;
    }

    const nextIndex = selectedIds.length;
    if (clickedIndex !== nextIndex) {
      const requiredDocument = orderedDocuments[nextIndex];
      setFeedback({
        type: "error",
        message: requiredDocument
          ? `Debes seleccionar primero la boleta más antigua (${requiredDocument.period}).`
          : "Debes seleccionar primero la boleta más antigua.",
      });
      return;
    }

    setSelectedIds([...selectedIds, id]);
    if (feedback?.type === "error") {
      setFeedback(null);
    }
  };

  const handleSelectAll = () => {
    const orderedIds = orderedDocuments.map((doc) => doc.id);
    if (selectedIds.length === orderedIds.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(orderedIds);
    if (feedback?.type === "error") {
      setFeedback(null);
    }
  };

  const handlePay = async () => {
    if (!contract || selectedIds.length === 0 || paying) return;

    setPaying(true);
    setFeedback(null);

    try {
      const result = await createPublicWebpayIntent({
        contractId: contract.id,
        documentIds: selectedIds,
        returnUrl: `${window.location.origin}/pagar`,
      });
      window.location.href = result.redirectUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : "No se pudo iniciar pago en Transbank";
      setFeedback({ type: "error", message });
      setPaying(false);
    }
  };

  const handleTransbankReturn = async () => {
    const url = new URL(window.location.href);
    const tokenWs = url.searchParams.get("token_ws");
    const cancelledToken = url.searchParams.get("TBK_TOKEN");

    if (!tokenWs && !cancelledToken) return;

    if (cancelledToken) {
      setFeedback({
        type: "error",
        message: "El pago fue cancelado o rechazado por Transbank.",
      });
      clearTransbankQueryParams(url);
      return;
    }

    if (!tokenWs) return;

    setPaying(true);
    setFeedback({
      type: "info",
      message: "Confirmando pago con Transbank...",
    });

    try {
      const commit = await commitPublicWebpay(tokenWs);
      if (commit.status === "approved") {
        setFeedback({ type: "success", message: "Pago confirmado correctamente." });
      } else {
        setFeedback({
          type: "error",
          message: "Transbank no confirmó el pago. Revisa el estado e intenta nuevamente.",
        });
      }

      const lastIdentifier = localStorage.getItem(LAST_SEARCH_KEY);
      if (lastIdentifier) {
        const payload = await searchPublicDebts(lastIdentifier);
        setContract(payload.contract);
        setDocuments(payload.documents);
        setSelectedIds([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "No se pudo confirmar el pago";
      setFeedback({ type: "error", message });
    } finally {
      setPaying(false);
      clearTransbankQueryParams(url);
    }
  };

  const clearTransbankQueryParams = (url: URL) => {
    url.searchParams.delete("token_ws");
    url.searchParams.delete("TBK_TOKEN");
    url.searchParams.delete("TBK_ORDEN_COMPRA");
    url.searchParams.delete("TBK_ID_SESION");
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white pt-20">
      <div className="bg-gradient-to-r from-sky-600 to-sky-500 text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20">
              <i className="ri-bank-card-line text-xl"></i>
            </div>
            <span className="text-sky-100 text-sm font-medium">Pago en línea</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Pagar cuenta de agua
          </h1>
          <p className="text-sky-100 text-sm md:text-base max-w-xl">
            Busca tus documentos pendientes por RUT o número de contrato, selecciona los que deseas pagar y procede con
            Transbank de forma segura.
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-6">
            {[
              { num: "1", label: "Busca tu contrato" },
              { num: "2", label: "Selecciona documentos" },
              { num: "3", label: "Paga con Transbank" },
            ].map((step, i) => (
              <div key={step.num} className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-sky-600 text-xs font-bold shrink-0">
                    {step.num}
                  </span>
                  <span className="text-xs font-medium text-white whitespace-nowrap">{step.label}</span>
                </div>
                {i < 2 && <i className="ri-arrow-right-line text-sky-300 text-sm"></i>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <SearchForm onResults={handleResults} onClear={handleClear} />

            {contract && (
              <DocumentList
                contract={contract}
                documents={orderedDocuments}
                selectedIds={selectedIds}
                nextSelectableIndex={selectedIds.length}
                onToggle={handleToggle}
                onSelectAll={handleSelectAll}
              />
            )}

            {!contract && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  {
                    icon: "ri-time-line",
                    title: "Disponible 24/7",
                    desc: "Paga en cualquier momento, sin necesidad de ir a la oficina.",
                    color: "text-sky-500 bg-sky-50",
                  },
                  {
                    icon: "ri-shield-check-line",
                    title: "Pago seguro",
                    desc: "Transacción cifrada y procesada por Transbank, la plataforma de pago más confiable de Chile.",
                    color: "text-emerald-500 bg-emerald-50",
                  },
                  {
                    icon: "ri-file-list-3-line",
                    title: "Múltiples boletas",
                    desc: "Selecciona y paga una o varias boletas en una sola transacción.",
                    color: "text-amber-500 bg-amber-50",
                  },
                ].map((card) => (
                  <div key={card.title} className="bg-white rounded-2xl border border-slate-100 p-5">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${card.color} mb-3`}>
                      <i className={`${card.icon} text-lg`}></i>
                    </div>
                    <h4 className="text-sm font-bold text-slate-800 mb-1">{card.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{card.desc}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <PaymentSummary
              selectedDocuments={selectedDocuments}
              paying={paying}
              onPay={handlePay}
              feedback={feedback}
            />
          </div>
        </div>

        <div className="mt-10 p-5 bg-sky-50 rounded-2xl border border-sky-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-100 text-sky-600 shrink-0">
            <i className="ri-question-line text-lg"></i>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-slate-800 mb-0.5">¿Necesitas ayuda?</h4>
            <p className="text-xs text-slate-500">
              Si tienes problemas para encontrar tu contrato o realizar el pago, contáctanos directamente.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <a
              href="/contacto"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-sky-300 text-sky-600 text-xs font-semibold hover:bg-sky-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>Contactar
            </a>
            <a
              href="https://wa.me/56900000000"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
